/**
 * Arriving on an invite link.
 *
 * Somebody pasted a link into a Discord and you pressed it. This says what
 * the party is before you are in it - the game, who is in it, how many seats
 * are left - and then puts you there. A link that has run out says so plainly
 * rather than dropping you on an empty screen.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Party } from '@/lib/types';
import { go } from '@/lib/hash';
import { gameBanner } from '@/lib/gameArt';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { Avatar } from '@/components/Avatar';
import { Button, Empty, useToast } from '@/components/ui';

export function JoinView({ token }: { token: string }) {
  const me = useSession((s) => s.me);
  const catalog = useSession((s) => s.catalog);
  const queryClient = useQueryClient();
  const toast = useToast();
  const [gone, setGone] = useState('');

  const q = useQuery({
    queryKey: ['join', token],
    queryFn: () => api.get<{ party: Party; full: boolean }>('/api/join/' + token),
    retry: false
  });

  const join = useMutation({
    mutationFn: () => api.post<{ party: Party }>('/api/join/' + token, {}),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['parties'] });
      toast('You are in', 'good');
      go('party/' + res.party.id);
    },
    onError: (err: Error) => {
      const e = err as Error & { code?: string };
      if (e.code === 'in-party') { toast(e.message, 'bad'); return; }
      setGone(e.message);
    }
  });

  if (q.isPending) {
    return (
      <div className="grid min-h-0 flex-1 place-items-center p-6">
        <div className="h-[260px] w-full max-w-[520px] animate-pulse rounded-[var(--radius-xl)] bg-surface-2" />
      </div>
    );
  }

  if (q.isError || gone) {
    return (
      <div className="grid min-h-0 flex-1 place-items-center p-6">
        <Empty
          icon="link"
          title="That invite link has expired"
          note={gone || 'Links run out on purpose. Ask for a new one, or find a party in the list.'}
          actions={<Button kind="primary" onClick={() => go('parties')}>Browse parties</Button>}
        />
      </div>
    );
  }

  const party = q.data!.party;
  const game = catalog?.games.find((g) => g.id === party.gameId);
  const art = gameBanner(party.gameId);
  const free = Math.max(0, party.size - party.memberCount);

  return (
    <div className="grid min-h-0 flex-1 place-items-center overflow-y-auto p-6">
      <section className="w-full max-w-[520px] overflow-hidden rounded-[var(--radius-xl)] bg-surface-1 shadow-[inset_0_0_0_1px_var(--line-soft),var(--shadow-2)]">
        <div className="relative h-[150px] bg-surface-3">
          {art ? <img src={art} alt="" className="h-full w-full object-cover" /> : null}
          <span aria-hidden className="absolute inset-0 bg-[linear-gradient(to_top,var(--bg-1),transparent_70%)]" />
        </div>

        <div className="p-5">
          <p className="m-0 text-[12px] font-bold tracking-[.18em] text-mute uppercase">You were invited</p>
          <h1 className="mt-1 mb-0 text-[26px] leading-tight font-extrabold tracking-[-.03em] text-ink">{party.title}</h1>
          <p className="mt-1.5 mb-0 flex flex-wrap items-center gap-x-2 text-[14px] text-mute">
            <span>{game?.name ?? 'A game'}</span>
            <span aria-hidden>·</span>
            <span>{party.mode || 'Any mode'}</span>
            <span aria-hidden>·</span>
            <span>{party.memberCount}/{party.size} players</span>
            {party.sessionLength ? <><span aria-hidden>·</span><span>{party.sessionLength === 'one' ? 'One match' : party.sessionLength === 'short' ? 'About an hour' : 'Long session'}</span></> : null}
          </p>

          <div className="mt-4 flex items-center gap-2">
            {party.members.slice(0, 6).map((m, i) => (
              m.user ? (
                <span key={m.userId} className="rounded-full ring-2 ring-[var(--bg-1)]" style={{ marginLeft: i ? -10 : 0 }}>
                  <Avatar user={m.user} size={34} />
                </span>
              ) : null
            ))}
            <span className="ml-1 text-[13px] text-mute">
              {free ? free + (free === 1 ? ' seat left' : ' seats left') : 'Full right now'}
            </span>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {!me ? (
              <Button kind="primary" onClick={() => go('home')}>Sign in to join</Button>
            ) : party.isMember ? (
              <Button kind="primary" onClick={() => go('party/' + party.id)}>Open the party</Button>
            ) : (
              <Button
                kind="primary"
                disabled={join.isPending || q.data!.full}
                icon={<Icon name="plus" size={16} />}
                onClick={() => join.mutate()}
              >
                {join.isPending ? 'Joining…' : q.data!.full ? 'The party is full' : 'Join the party'}
              </Button>
            )}
            <Button kind="outline" onClick={() => go('parties')}>See other parties</Button>
          </div>
        </div>
      </section>
    </div>
  );
}
